import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import FormActions from '../../../components/FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import AssignmentDropdown from '../../../components/app-agent/AssignmentDropdown';
import { translate } from '../../../services/translate';
import FilterSwitch from '../../../components/Filter/components/FilterSwitch';
import MyAppText from '../../../components/MyAppText';
import { faCheck, faClock, faExclamationCircle, faExclamationTriangle, faInfoCircle, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ScheduleDropdown from './ScheduleDropdown';
import { ScrollView } from 'react-native-web-hover';
import UnitFinishEarlyDropdown from '../../../components/app-agent/UnitFinishEarlyDropdown';
import { useAuth } from '../../../context/Auth';
import moment from 'moment';
import { CustomTooltip } from '../../../components/CustomTooltip';
import { VehicleChecklistMoment, VehicleChecklistValuesEnum } from '../../../services/central-api/dispatch';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedAgentId: number;
    setUnit: Dispatch<SetStateAction<AppAgentUnit | undefined>>;
    unit: AppAgentUnit;
    onConfirm: (checklist?: CreateUpdateChecklist['checklist']) => void;
    type: 'teamActivation' | 'agentActivation' | 'agentEdit';
}

const MIN_CHECKED_ITEMS = 1;

export default function EditAgentModal({ selectedAgentId, isModalVisible, setModalVisible, unit, setUnit, onConfirm, type }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    if (!authenticatedUser) {
        return;
    }

    const selectedAgent = useMemo(() => {
        return unit.UnitUsers.find(user => user.id === selectedAgentId);
    }, [unit, selectedAgentId]);

    const rollbackUnit = useMemo(() => {
        return unit;
    }, [isModalVisible]);

    const [agentActive, setAgentActive] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);

    const [checkedItems, setCheckedItems] = useState<VehicleChecklistValuesEnum[]>([]);

    const checklistItems = useMemo(() => {
        if (!unit.unitName.unitCategory.generateOSE) {
            return [];
        }

        return unit.unitName.unitCategory.checklist?.filter(item => item.moment === VehicleChecklistMoment.BEFORE_DEPARTURE) ?? [];
    }, [unit]);

    const hasChecklistStep = type === 'teamActivation' && checklistItems.length > 0;
    const totalSteps = hasChecklistStep ? 2 : 1;
    const isLastStep = step >= totalSteps;

    useEffect(() => {
        if (isModalVisible && selectedAgent) {
            if (type !== 'agentEdit') {
                setAgentActive(true);
                if (!selectedAgent.active || selectedAgent.unitId !== unit.id) {
                    setUnit(prevUnit => ({
                        ...prevUnit!,
                        UnitUsers: prevUnit?.UnitUsers.map(user =>
                            user.id === selectedAgentId
                                ? { ...user, unitId: prevUnit.id, active: true }
                                : user
                        ) ?? []
                    }));
                }
            } else {
                setAgentActive(selectedAgent.active);
            }
        }
    }, [isModalVisible, selectedAgent, unit]);

    const resetModalState = () => {
        setStep(1);
        setCheckedItems([]);
        setAgentActive(false);
    };

    function toggleChecklistItem(value: VehicleChecklistValuesEnum) {
        setCheckedItems(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    }

    const buildChecklist = (): CreateUpdateChecklist['checklist'] => {
        const values = Object.fromEntries(
            checklistItems.map(item => [item.checklistValue, checkedItems.includes(item.checklistValue)])
        ) as Partial<VehicleChecklistValues>;

        return { [VehicleChecklistMoment.BEFORE_DEPARTURE]: values };
    };

    const closeModal = () => {
        onConfirm(hasChecklistStep ? buildChecklist() : undefined);
        setModalVisible(false);
        resetModalState();
    };

    const cancelModal = () => {
        setModalVisible(false);
        resetModalState();
        setUnit(rollbackUnit);
    };

    if (!selectedAgent) {
        return <></>;
    }

    function isFormValid() {
        if (!selectedAgent) {
            return false;
        } else if (selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id) {
            return false;
        } else if (!selectedAgent.active && !selectedAgent.UnitUser.unitFinishEarlyReasonId) {
            return false;
        } else if (selectedAgent.active && !selectedAgent.UnitUser.agentScheduleId) {
            return false;
        } else if (unit.UnitUsers.every(user => !user.active)) {
            return false;
        } else if (step === 2 && hasChecklistStep && checkedItems.length < MIN_CHECKED_ITEMS) {
            return false;
        }

        return true;
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={cancelModal}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        {step === 1 && <MyAppText style={styles.header}>{translate(type === 'teamActivation' ? 'teamActivation' : 'agentEdit')}</MyAppText>}
                        {step === 2 && <>
                            <MyAppText style={styles.header}>{translate('vehicleChecklist')}</MyAppText>
                            <MyAppText style={styles.subheader}>{translate('vehicleChecklistMessage')}</MyAppText>
                        </>}
                        {type === 'teamActivation' && totalSteps > 1
                            ? <MyAppText style={styles.stepText}>{translate('stepXofY', { x: step, y: totalSteps })}</MyAppText>
                            : <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                        }
                    </View>
                    {step === 1 && <View style={styles.unitPreviewBox}>
                        <View style={{ flexDirection: 'column', gap: 8 }}>
                            <MyAppText style={styles.unitPreviewBoxUnitName}>{unit.unitName.name}</MyAppText>
                            <View style={{ flexDirection: 'column', gap: 4 }}>
                                <View style={styles.unitPreviewBoxDescriptionRow}>
                                    <FontAwesomeIcon icon={faClock} fontSize={14} color={theme.colors.neutral['400']} />
                                    <MyAppText style={styles.unitPreviewBoxDescription}>{`${moment(unit.estimatedStart).format('DD/MM/YY - HH:mm')} ${translate('until')} ${moment(unit.estimatedFinish).format('DD/MM/YY - HH:mm')}`}</MyAppText>
                                </View>

                                {unit.unitName.plate && <View style={styles.unitPreviewBoxDescriptionRow}>
                                    <FontAwesomeIcon icon={faInfoCircle} fontSize={14} color={theme.colors.neutral['400']} />
                                    <MyAppText style={styles.unitPreviewBoxDescription}>{`${unit.unitName.unitCategory.name} - [${unit.unitName.plate}]`}</MyAppText>
                                </View>}

                                <View style={styles.unitPreviewBoxDescriptionRow}>
                                    <FontAwesomeIcon icon={faUsers} fontSize={14} color={theme.colors.neutral['400']} />
                                    <MyAppText style={styles.unitPreviewBoxDescription}>{`${unit.UnitUsers.length} ${translate(unit.UnitUsers.length > 1 ? 'agents' : 'agent')}`}</MyAppText>
                                </View>
                            </View>
                        </View>
                    </View>}
                    <ScrollView>
                        {step === 1 && <View style={{ flexDirection: 'column', rowGap: 16 }}>
                            {type === 'agentEdit' && <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.fieldLabel}>{translate('agentStatus')}</MyAppText>
                                <FilterSwitch
                                    value={agentActive}
                                    label={translate('active')}
                                    onChange={(value) => {
                                        setAgentActive(value);
                                        setUnit(prevUnit => ({
                                            ...prevUnit!,
                                            UnitUsers: prevUnit?.UnitUsers.map(user =>
                                                user.id === selectedAgentId
                                                    ? {
                                                        ...user,
                                                        unitId: value ? prevUnit.id : null,
                                                        active: Boolean(value)
                                                    }
                                                    : user
                                            ) ?? []
                                        }));
                                    }}
                                    useEnhancedStyle
                                    direction='row'
                                    side='right'
                                    disabled={selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id}
                                />
                            </View>}
                            {type === 'agentEdit' && unit?.UnitUsers.every(user => !user.active) ?
                                <View style={styles.cannotEditAgentContainer}>
                                    <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#B8401B' }} />
                                    <MyAppText>{translate('cannotSaveTeamWithoutActivatedUsers')}</MyAppText>
                                </View> : <></>
                            }
                            {type === 'agentEdit' && selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id ?
                                <View style={styles.cannotEditAgentContainer}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} color='#B8401B' />
                                    <MyAppText>{translate('agentActiveOtherTeam')}</MyAppText>
                                </View>
                                : <></>
                            }
                            <FormInput
                                disabled={true}
                                onChangeText={() => { }}
                                value={selectedAgent.warName || selectedAgent.name}
                                label={translate('name')}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                            />
                            <FormInput
                                disabled={true}
                                onChangeText={() => { }}
                                value={selectedAgent.registry}
                                label={translate('registry')}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                            />
                            <AssignmentDropdown
                                setValue={(value) => {
                                    setUnit(prevUnit => ({
                                        ...prevUnit!,
                                        UnitUsers: prevUnit?.UnitUsers.map(user =>
                                            user.id === selectedAgentId
                                                ? {
                                                    ...user,
                                                    UnitUser: {
                                                        ...user.UnitUser,
                                                        assignmentId: value(user.UnitUser.assignmentId || 0),
                                                    },
                                                }
                                                : user
                                        ) ?? []
                                    }));
                                }}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                                value={selectedAgent.UnitUser.assignmentId || 0}
                                zIndex={3}
                                disabled={selectedAgent.unitId !== null && selectedAgent.unitId !== unit.id}
                            />
                            {!agentActive ? <UnitFinishEarlyDropdown
                                value={selectedAgent.UnitUser.unitFinishEarlyReasonId || 0}
                                setValue={(value) => {
                                    setUnit(prevUnit => ({
                                        ...prevUnit!,
                                        UnitUsers: prevUnit?.UnitUsers.map(user =>
                                            user.id === selectedAgentId
                                                ? {
                                                    ...user,
                                                    UnitUser: {
                                                        ...user.UnitUser,
                                                        unitFinishEarlyReasonId: value(user.UnitUser.unitFinishEarlyReasonId || 0),
                                                    },
                                                }
                                                : user
                                        ) ?? []
                                    }));
                                }}
                                unitId={unit.id}
                                labelStyle={styles.fieldLabel}
                                viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                            />
                                : <ScheduleDropdown
                                    labelStyle={styles.fieldLabel}
                                    viewStyle={{ flexDirection: 'column', rowGap: 8 }}
                                    value={selectedAgent.UnitUser.agentScheduleId || 0}
                                    setValue={(value) => {
                                        setUnit(prevUnit => ({
                                            ...prevUnit!,
                                            UnitUsers: prevUnit?.UnitUsers.map(user =>
                                                user.id === selectedAgentId
                                                    ? {
                                                        ...user,
                                                        UnitUser: {
                                                            ...user.UnitUser,
                                                            agentScheduleId: value(user.UnitUser.agentScheduleId || 0),
                                                        },
                                                    }
                                                    : user
                                            ) ?? []
                                        }));
                                    }}
                                    zIndex={2}
                                />
                            }
                        </View>}
                        {step === 2 && <View style={{ flexDirection: 'column', rowGap: 16 }}>
                            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                <MyAppText>{translate('before_departure')}</MyAppText>
                                <CustomTooltip
                                    content={translate('vehicleChecklistTooltip')}
                                />
                            </View>
                            <View style={styles.checklistList}>
                                {checklistItems.map(item => {
                                    const selected = checkedItems.includes(item.checklistValue);
                                    return (
                                        <TouchableOpacity
                                            key={item.id}
                                            style={styles.checklistRow}
                                            onPress={() => toggleChecklistItem(item.checklistValue)}
                                            accessibilityRole='checkbox'
                                            accessibilityState={{ checked: selected }}
                                        >
                                            <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                                                {selected && <FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                                            </View>
                                            <MyAppText style={styles.checklistLabel}>{translate(item.checklistValue)}</MyAppText>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </View>}
                    </ScrollView>
                    <FormActions
                        onSubmit={() => {
                            if (isLastStep) {
                                closeModal();
                            } else {
                                setStep(step + 1);
                            }
                        }}
                        onClose={() => {
                            if (step === 1) {
                                cancelModal();
                            } else {
                                setStep(step - 1);
                            }
                        }}
                        confirmText={isLastStep ? 'conclude' : 'forward'}
                        cancelText={step === 1 ? 'cancel' : 'goBack'}
                        disabled={!isFormValid()}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        buttonContentContainerStyle={{ flex: 1 }}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        width: '100%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 24,
        maxHeight: '95%',
        overflow: 'hidden',
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.neutral['500'],
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.neutral['400'],
    },
    fieldLabel: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    cannotEditAgentContainer: {
        flexDirection: 'row',
        gap: 10,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderRadius: 8,
        borderColor: '#B8401B',
        padding: 10
    },
    stepText: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    unitPreviewBox: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#F3E6CC',
        backgroundColor: '#FEFCF7',
        padding: 12
    },
    unitPreviewBoxUnitName: {
        color: theme.colors.neutral['500'],
        fontWeight: '700',
        fontSize: 14,
    },
    unitPreviewBoxDescriptionRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    },
    unitPreviewBoxDescription: {
        color: theme.colors.neutral['400'],
        fontWeight: '600',
        fontSize: 14,
    },
    checklistList: {
        gap: 12,
        paddingLeft: 16
    },
    checklistRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    checklistLabel: {
        fontSize: 16,
        color: theme.colors.labelColor
    },
}));
